/**
 * Customer Profile Section JavaScript
 * Handles ticker form submission to Make webhook and favorites management
 */

/**
 * SortableTable - Handles table sorting by column click
 */
class SortableTable {
  constructor(table) {
    this.table = table;
    this.headers = table.querySelectorAll('th.sortable');
    this.tbody = table.querySelector('tbody');
    this.rows = Array.from(this.tbody.querySelectorAll('tr'));
    this.currentSort = { column: null, direction: 'asc' };
    this.emptyLastColumns = ['precio', 'precio_6m', 'precio_12m', 'precio_24m', 'var_6m', 'var_12m', 'var_24m'];

    this.init();
  }

  init() {
    this.headers.forEach(header => {
      header.addEventListener('click', () => {
        const column = header.dataset.column;

        if (this.currentSort.column === column) {
          this.currentSort.direction = this.currentSort.direction === 'asc' ? 'desc' : 'asc';
        } else {
          this.currentSort.column = column;
          this.currentSort.direction = 'asc';
        }

        this.updateSortIndicators(header);
        this.sortTable(column, this.currentSort.direction);
      });
    });
  }

  updateSortIndicators(activeHeader) {
    this.headers.forEach(header => {
      const arrow = header.querySelector('.sort-arrow');
      arrow.className = 'sort-arrow';
      if (header === activeHeader) {
        arrow.classList.add(this.currentSort.direction);
      }
    });
  }

  isCellEmpty(row, column) {
    const cell = row.querySelector(`[data-col="${column}"]`);
    if (!cell) return true;
    const raw = cell.getAttribute('data-value');
    if (!raw) return true;
    const num = parseFloat(raw);
    return isNaN(num) || num === 0;
  }

  sortTable(column, direction) {
    this.rows.sort((a, b) => {
      // Empty-last logic for price/variation columns
      if (this.emptyLastColumns.includes(column)) {
        const emptyA = this.isCellEmpty(a, column);
        const emptyB = this.isCellEmpty(b, column);
        if (emptyA && !emptyB) return 1;
        if (!emptyA && emptyB) return -1;
        if (emptyA && emptyB) return 0;
      }

      let aValue, bValue;

      switch (column) {
        case 'ticker':
          aValue = a.querySelector('.ticker-cell strong')?.textContent.trim() || '';
          bValue = b.querySelector('.ticker-cell strong')?.textContent.trim() || '';
          break;
        case 'name':
          aValue = a.querySelector('.company-name')?.textContent.trim() || '';
          bValue = b.querySelector('.company-name')?.textContent.trim() || '';
          break;
        case 'score':
          aValue = parseInt(a.querySelector('.score-cell')?.dataset.score) || 0;
          bValue = parseInt(b.querySelector('.score-cell')?.dataset.score) || 0;
          break;
        case 'precio':
        case 'precio_6m':
        case 'precio_12m':
        case 'precio_24m':
        case 'var_6m':
        case 'var_12m':
        case 'var_24m': {
          const aCell = a.querySelector(`[data-col="${column}"]`);
          const bCell = b.querySelector(`[data-col="${column}"]`);
          aValue = parseFloat(aCell?.dataset.value) || 0;
          bValue = parseFloat(bCell?.dataset.value) || 0;
          break;
        }
        case 'fundamentos':
        case 'crecimiento':
        case 'rentabilidad':
        case 'valoracion':
        case 'eficiencia':
        case 'momentum': {
          const aCells = Array.from(a.querySelectorAll('.subscore'));
          const bCells = Array.from(b.querySelectorAll('.subscore'));
          const columnIndex = ['fundamentos', 'crecimiento', 'rentabilidad', 'valoracion', 'eficiencia', 'momentum'].indexOf(column);
          aValue = parseInt(aCells[columnIndex]?.dataset.score) || 0;
          bValue = parseInt(bCells[columnIndex]?.dataset.score) || 0;
          break;
        }
        case 'report_date':
          aValue = a.querySelector('.report-date')?.textContent.trim() || '';
          bValue = b.querySelector('.report-date')?.textContent.trim() || '';
          if (aValue === '-') aValue = '';
          if (bValue === '-') bValue = '';
          break;
        default:
          return 0;
      }

      let result;
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        result = aValue.localeCompare(bValue, 'es', { numeric: true });
      } else {
        result = aValue - bValue;
      }

      return direction === 'desc' ? -result : result;
    });

    // Re-append sorted rows
    this.rows.forEach(row => this.tbody.appendChild(row));

    // Re-colorize variation cells after sort
    colorizeVariationCells();
  }
}

/**
 * Colorize variation cells with red-to-green gradient scale
 */
function colorizeVariationCells() {
  function getVarColor(value) {
    if (isNaN(value) || value === 0) return null;
    var t = Math.max(-30, Math.min(30, value)) / 30;
    var abs = Math.abs(t);
    var sat = Math.round(abs * 72 + 8);
    var light = Math.round(44 - abs * 8);
    var hue;
    if (t < 0) {
      hue = Math.round(25 * (1 - abs));
    } else {
      hue = Math.round(100 + abs * 40);
    }
    return 'hsl(' + hue + ',' + sat + '%,' + light + '%)';
  }
  var cells = document.querySelectorAll('.customer-profile .var-cell[data-value]:not([data-locked])');
  for (var i = 0; i < cells.length; i++) {
    var val = parseFloat(cells[i].getAttribute('data-value'));
    var color = getVarColor(val);
    if (color) cells[i].style.color = color;
  }
}

class CustomerProfile {
  constructor() {
    this.tickerForm = document.getElementById('tickers-form');
    this.init();
  }

  init() {
    this.initTickerForm();
    this.initSortableTables();
    this.initPremiumHandlers();
    this.initMyRankings();
  }

  /**
   * Initialize sortable tables
   */
  initSortableTables() {
    const tables = document.querySelectorAll('#tickersTable, #favoritesTable');
    tables.forEach(table => {
      if (table) {
        new SortableTable(table);
        // Observe tbody for re-colorization after DOM changes
        const tbody = table.querySelector('tbody');
        if (tbody) {
          new MutationObserver(colorizeVariationCells).observe(tbody, { childList: true });
        }
      }
    });
    // Initial colorization
    colorizeVariationCells();
  }

  /**
   * Initialize premium tooltip and click handlers for locked cells
   */
  initPremiumHandlers() {
    const tables = document.querySelectorAll('#favoritesTable, #tickersTable');
    const premiumUrl = tables[0]?.dataset.premiumUrl || '/pages/onboarding';
    const tooltip = document.getElementById('premiumTooltipProfile');

    // Click handler for locked cells
    document.addEventListener('click', function(e) {
      var lockedCell = e.target.closest('.customer-profile td[data-locked="true"]');
      if (lockedCell) {
        e.preventDefault();
        e.stopPropagation();
        window.location.href = premiumUrl;
      }
    });

    // Tooltip hover
    if (tooltip) {
      tooltip.style.position = 'fixed';
      document.addEventListener('mouseenter', function(e) {
        var lockedCell = e.target.closest('.customer-profile td[data-locked="true"]');
        if (!lockedCell) return;
        var rect = lockedCell.getBoundingClientRect();
        tooltip.style.display = 'block';
        var tipW = tooltip.offsetWidth;
        var tipH = tooltip.offsetHeight;
        var left = rect.left + rect.width / 2 - tipW / 2;
        left = Math.max(4, Math.min(left, window.innerWidth - tipW - 4));
        tooltip.style.left = left + 'px';
        tooltip.style.top = (rect.top - tipH - 6) + 'px';
      }, true);

      document.addEventListener('mouseleave', function(e) {
        if (e.target.closest && e.target.closest('.customer-profile td[data-locked="true"]')) {
          tooltip.style.display = 'none';
        }
      }, true);
    }
  }

  /**
   * Initialize "Mis Rankings" section using RankingCardRenderer
   */
  initMyRankings() {
    const sectionEl = document.querySelector('.customer-rankings[data-section-id]') ||
                      document.querySelector('.customer-profile[data-section-id]');
    if (!sectionEl) return;

    const sectionId = sectionEl.dataset.sectionId;
    if (!sectionId) return;

    const configScript = document.getElementById(`my-rankings-config-${sectionId}`);
    const grid = document.getElementById(`myRankingsGrid-${sectionId}`);
    if (!configScript || !grid) return;

    if (typeof window.RankingCardRenderer === 'undefined') {
      console.error('CustomerProfile: ranking-card-shared.js not loaded');
      return;
    }

    try {
      const config = JSON.parse(configScript.textContent);
      const products = config.products || [];
      const settings = config.settings || {};
      const renderer = new window.RankingCardRenderer(settings);

      if (products.length === 0) {
        renderer.showEmpty(grid, settings.emptyTitle, settings.emptyText);
        return;
      }

      // Sort by report_date descending (most recent first)
      products.sort((a, b) => {
        const dateA = a.report_date ? new Date(a.report_date) : new Date(0);
        const dateB = b.report_date ? new Date(b.report_date) : new Date(0);
        return dateB - dateA;
      });

      this.enrichAndRenderRankings(renderer, products, grid);
    } catch (e) {
      console.error('CustomerProfile: Error initializing rankings', e);
      if (grid) grid.innerHTML = '<div class="latest-rankings__error"><p>Error al cargar los rankings</p></div>';
    }
  }

  /**
   * Fetch sub-collection stats and render ranking cards
   */
  async enrichAndRenderRankings(renderer, products, grid) {
    const { shopDomain, storefrontAccessToken } = window.StocksReportConfig || {};

    if (!storefrontAccessToken) {
      console.warn('CustomerProfile: Storefront Access Token not configured for rankings');
      // Render cards without stats
      grid.innerHTML = '';
      products.forEach(ranking => {
        const card = renderer.createCardElement({ ...ranking, stats: null });
        grid.appendChild(card);
      });
      renderer.setupCardObservers(Array.from(grid.querySelectorAll('.latest-rankings__card')), grid);
      return;
    }

    const enrichedRankings = await Promise.all(
      products.map(async (ranking) => {
        if (!ranking.subCollectionHandle) return { ...ranking, stats: null };

        try {
          const companyProducts = await renderer.fetchSubCollectionProducts(
            shopDomain, storefrontAccessToken, ranking.subCollectionHandle
          );

          if (companyProducts.length === 0) return { ...ranking, stats: null };

          const stats = renderer.computeRankingStats(companyProducts);
          return { ...ranking, stats, productsCount: companyProducts.length };
        } catch (e) {
          console.warn(`CustomerProfile: Failed to fetch sub-collection "${ranking.subCollectionHandle}"`, e);
          return { ...ranking, stats: null };
        }
      })
    );

    grid.innerHTML = '';

    enrichedRankings.forEach(ranking => {
      const card = renderer.createCardElement(ranking);
      grid.appendChild(card);
    });

    const cards = grid.querySelectorAll('.latest-rankings__card');
    renderer.setupCardObservers(Array.from(cards), grid);
  }

  /**
   * Initialize ticker form handling
   */
  initTickerForm() {
    if (!this.tickerForm) return;

    // Get config from data attributes
    this.customerId = this.tickerForm.dataset.customerId;
    this.webhookUrl = this.tickerForm.dataset.webhookUrl;
    this.shouldReload = this.tickerForm.dataset.successReload === 'true';
    this.errorMessage = this.tickerForm.dataset.errorMessage || 'Error al enviar la solicitud. Por favor, inténtalo de nuevo.';

    // Parse pending tickers from data attribute
    try {
      this.pendingTickers = JSON.parse(this.tickerForm.dataset.pendingTickers || '[]');
    } catch (e) {
      this.pendingTickers = [];
      console.error('Error parsing pending tickers:', e);
    }

    // Auto-uppercase ticker inputs
    const tickerInputs = this.tickerForm.querySelectorAll('.ticker-field__input');
    tickerInputs.forEach(input => {
      input.addEventListener('input', (e) => {
        e.target.value = e.target.value.toUpperCase().replace(/[^A-Z0-9.]/g, '');
      });

      // Visual feedback on filled inputs
      input.addEventListener('blur', (e) => {
        const wrapper = e.target.closest('.ticker-field');
        if (e.target.value.trim()) {
          wrapper.classList.add('ticker-field--filled');
        } else {
          wrapper.classList.remove('ticker-field--filled');
        }
      });
    });

    // Form submission
    this.tickerForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      await this.handleTickerSubmit(e);
    });
  }

  /**
   * Handle ticker form submission to Make webhook
   */
  async handleTickerSubmit(e) {
    const form = e.target;
    const submitBtn = form.querySelector('.tickers-form__submit');
    const submitText = submitBtn.querySelector('.tickers-form__submit-text');
    const submitLoading = submitBtn.querySelector('.tickers-form__submit-loading');
    const messageWrapper = form.querySelector('.tickers-form__message');
    const successDiv = messageWrapper.querySelector('.tickers-form__success');

    // Get new tickers from inputs
    const tickerInputs = form.querySelectorAll('.ticker-field__input');
    const newTickers = [];

    tickerInputs.forEach((input) => {
      const value = input.value.trim().toUpperCase();
      if (value && /^[A-Z0-9.]{1,10}$/.test(value)) {
        newTickers.push(value);
      }
    });

    // Combine pending tickers + new tickers
    const allTickers = [...this.pendingTickers, ...newTickers];

    // Validate we have at least one new ticker
    if (newTickers.length === 0) {
      this.showFormError(form, 'Por favor, introduce al menos un ticker.');
      return;
    }

    // Check for duplicates
    const uniqueTickers = [...new Set(allTickers)];
    if (uniqueTickers.length !== allTickers.length) {
      this.showFormError(form, 'Hay tickers duplicados. Por favor, revisa tu selección.');
      return;
    }

    // Validate webhook URL is configured
    if (!this.webhookUrl) {
      this.showFormError(form, 'Error de configuración. Contacta con soporte.');
      console.error('Webhook URL not configured');
      return;
    }

    // Show loading state
    submitBtn.disabled = true;
    submitText.style.display = 'none';
    submitLoading.style.display = 'inline-flex';

    try {
      // Build payload for Make webhook
      const tickersValue = JSON.stringify(uniqueTickers);

      // Build Shopify Admin API body for metafield upsert
      const shopifyBody = JSON.stringify({
        metafield: {
          namespace: "custom",
          key: "selected_tickers",
          type: "list.single_line_text_field",
          value: tickersValue
        }
      });

      const payload = {
        customer_id: String(this.customerId),
        shopify_body: shopifyBody
      };

      console.log('[Tickers] Sending to webhook:', this.webhookUrl, payload);

      // Send to Make webhook — use text/plain to avoid CORS preflight issues
      const response = await fetch(this.webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'text/plain;charset=UTF-8',
        },
        body: JSON.stringify(payload)
      });

      console.log('[Tickers] Webhook response:', response.status, response.statusText);

      if (!response.ok) {
        const responseText = await response.text().catch(() => '');
        console.error('[Tickers] Webhook error body:', responseText);
        throw new Error(`Webhook HTTP ${response.status}: ${responseText || response.statusText}`);
      }

      // Show success message
      this.hideFormError(form);
      successDiv.style.display = 'flex';
      messageWrapper.style.display = 'block';

      // Mark inputs as submitted
      tickerInputs.forEach(input => {
        if (input.value.trim()) {
          const wrapper = input.closest('.ticker-field');
          wrapper.classList.add('ticker-field--submitted');
          input.disabled = true;
        }
      });

      // Disable submit button
      submitBtn.disabled = true;
      submitBtn.classList.add('button--disabled');

      // Scroll to success message
      messageWrapper.scrollIntoView({ behavior: 'smooth', block: 'center' });

      // Reload page after delay to give Make time to update the metafield
      if (this.shouldReload) {
        setTimeout(() => {
          window.location.reload();
        }, 3500);
      }

    } catch (error) {
      console.error('[Tickers] Error submitting ticker form:', error);
      this.showFormError(form, this.errorMessage);
      submitBtn.disabled = false;
    } finally {
      submitText.style.display = 'inline';
      submitLoading.style.display = 'none';
    }
  }

  /**
   * Show form error message
   */
  showFormError(form, message) {
    const messageWrapper = form.querySelector('.tickers-form__message');
    const successDiv = messageWrapper.querySelector('.tickers-form__success');

    // Create or update error message
    let errorDiv = messageWrapper.querySelector('.tickers-form__error');
    if (!errorDiv) {
      errorDiv = document.createElement('div');
      errorDiv.className = 'tickers-form__error';
      errorDiv.innerHTML = `
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
        <span></span>
      `;
      messageWrapper.appendChild(errorDiv);
    }

    errorDiv.querySelector('span').textContent = message;
    errorDiv.style.display = 'flex';
    if (successDiv) successDiv.style.display = 'none';
    messageWrapper.style.display = 'block';

    // Add error styles
    errorDiv.style.cssText = `
      display: flex;
      align-items: center;
      gap: 0.8rem;
      padding: 1.2rem 1.5rem;
      background: #fef2f2;
      border-radius: 0.8rem;
      color: #991b1b;
      font-size: 1.4rem;
      font-weight: 500;
    `;
    errorDiv.querySelector('svg').style.cssText = `
      flex-shrink: 0;
      color: #ef4444;
    `;

    // Auto-hide after 5 seconds
    setTimeout(() => {
      this.hideFormError(form);
    }, 5000);
  }

  /**
   * Hide form error message
   */
  hideFormError(form) {
    const messageWrapper = form.querySelector('.tickers-form__message');
    const errorDiv = messageWrapper.querySelector('.tickers-form__error');
    if (errorDiv) {
      errorDiv.style.display = 'none';
    }
  }

}

// Initialize when DOM is ready (guard against duplicate script includes from multiple sections)
if (!window._customerProfileInitialized) {
  window._customerProfileInitialized = true;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new CustomerProfile());
  } else {
    new CustomerProfile();
  }
}

// Shopify Theme Editor support
if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (e) => {
    if (e.target.classList.contains('customer-profile-section')) {
      window._customerProfileInitialized = false;
      new CustomerProfile();
      window._customerProfileInitialized = true;
    }
  });
}
